import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, Clock, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { valueLabel } from "@/i18n";

export default function EvaluatorHome() {
  const { t } = useTranslation();
  const [apps, setApps] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/evaluator/applications").then((r) => setApps(r.data)).catch(() => setApps([]));
  }, []);

  if (apps === null) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const pending = apps.filter((a) => !a.reviewed);
  const done = apps.filter((a) => a.reviewed);

  return (
    <div className="animate-fade-in">
      <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] mb-1">{t("evaluator.home.eyebrow")}</p>
      <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">{t("evaluator.home.title")}</h1>
      <p className="text-muted-foreground mb-8">
        {apps.length === 0
          ? t("evaluator.home.none")
          : t("evaluator.home.counts", { pending: pending.length, done: done.length })}
      </p>

      {apps.length > 0 && (
        <div className="space-y-3">
          {[...pending, ...done].map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(`/evaluator/applications/${a.id}`)}
              className="w-full flex items-center justify-between bg-card border border-border rounded-xl p-5 hover:border-primary hover:shadow-sm transition-all text-left"
              data-testid={`eval-app-${a.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-lg font-medium">{a.name} {a.surname}</span>
                  {a.reviewed ? (
                    <span className="inline-flex items-center gap-1 text-xs text-primary bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5" data-testid={`reviewed-badge-${a.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> {t("evaluator.home.reviewed", { score: a.my_overall })}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-accent bg-accent/10 border border-accent/20 rounded-full px-2 py-0.5">
                      <Clock className="w-3 h-3" /> {t("evaluator.home.pending")}
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground truncate mt-1">
                  {valueLabel(a.university)} · {valueLabel(a.field_of_study)?.split(" (")[0]} · {t("common.gpa", { gpa: a.gpa || "—" })}
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
